import React, { useState } from "react";
import {
  X,
  Settings,
  Search,
  Eye,
  Edit,
  Plus,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Power,
  DollarSign,
  Percent,
  Tag,
  Activity,
  TrendingUp,
  Trash2,
  Save,
} from "lucide-react";
import { Layout } from "../../layouts/DashboardLayout";

// Types
interface Rate {
  id: string;
  service: "standard" | "express" | "p2p" | "exchange";
  serviceName: string;
  country?: string;
  countryFlag?: string;
  feeType: "percentage" | "fixed";
  feeValue: number;
  minFee?: number;
  maxFee?: number;
  status: "active" | "inactive" | "expired";
  effectiveDate: string;
  expiryDate?: string;
  usage: {
    transactionsCount: number;
    revenue: number;
  };
}

interface PromoCode {
  id: string;
  code: string;
  description: string;
  discountType: "percentage" | "fixed";
  discountValue: number;
  usageCount: number;
  validFrom: string;
  validTo: string;
}

interface FilterState {
  search: string;
  service: string;
  status: string;
}

interface RateItemProps {
  rate: Rate;
  onAction: (action: string, rate: Rate) => void;
}

interface RatesListProps {
  rates: Rate[];
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  onRateAction: (action: string, rate: Rate) => void;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  startIndex: number;
  endIndex: number;
  totalRates: number;
}

interface RateDetailsProps {
  rate: Rate | null;
  onClose: () => void;
  onSave: (rate: Rate) => void;
  onToggleStatus: () => void;
}

// PromoCodeModal Component
interface PromoCodeModalProps {
  promo: PromoCode | null;
  onClose: () => void;
  onSave: (promo: PromoCode) => void;
}

const PromoCodeModal: React.FC<PromoCodeModalProps> = ({ promo, onClose, onSave }) => {
  const [formData, setFormData] = useState<PromoCode>(
    promo || {
      id: Date.now().toString(),
      code: "",
      description: "",
      discountType: "percentage",
      discountValue: 0,
      usageCount: 0,
      validFrom: new Date().toISOString().split("T")[0],
      validTo: new Date().toISOString().split("T")[0],
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">{promo ? "Modifier le code promo" : "Nouveau code promo"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Code promo</label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              required
              placeholder="EX: PROMO2024"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              required
              placeholder="Description du code promo"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type de réduction</label>
            <select
              value={formData.discountType}
              onChange={(e) => setFormData({ ...formData, discountType: e.target.value as "percentage" | "fixed" })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="percentage">Pourcentage (%)</option>
              <option value="fixed">Montant fixe (CAD)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Valeur de la réduction {formData.discountType === "percentage" ? "(%)" : "(CAD)"}
            </label>
            <input
              type="number"
              value={formData.discountValue}
              onChange={(e) => setFormData({ ...formData, discountValue: parseFloat(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              required
              min="0"
              step={formData.discountType === "percentage" ? "1" : "0.01"}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
              <input
                type="date"
                value={formData.validFrom}
                onChange={(e) => setFormData({ ...formData, validFrom: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
              <input
                type="date"
                value={formData.validTo}
                onChange={(e) => setFormData({ ...formData, validTo: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                required
              />
            </div>
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {promo ? "Modifier" : "Créer"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ConfirmDeleteModal Component
interface ConfirmDeleteModalProps {
  promoCode: string;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({ promoCode, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex items-center space-x-3 mb-4">
          <div className="bg-red-100 p-2 rounded-full">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-gray-800">Confirmer la suppression</h2>
        </div>
        <p className="text-gray-600 mb-6">
          Êtes-vous sûr de vouloir supprimer le code promo <span className="font-semibold">{promoCode}</span> ? Cette
          action est irréversible.
        </p>
        <div className="flex space-x-3">
          <button
            onClick={onConfirm}
            className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
          >
            Supprimer
          </button>
          <button
            onClick={onCancel}
            className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
          >
            Annuler
          </button>
        </div>
      </div>
    </div>
  );
};

// Utility Components
const ServiceBadge: React.FC<{ service: Rate["service"] }> = ({ service }) => {
  const serviceConfig = {
    standard: { bg: "bg-blue-100", text: "text-blue-700", label: "Standard" },
    express: { bg: "bg-purple-100", text: "text-purple-700", label: "Express" },
    p2p: { bg: "bg-green-100", text: "text-green-700", label: "P2P" },
    exchange: { bg: "bg-orange-100", text: "text-orange-700", label: "Change" },
  };

  const config = serviceConfig[service];

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>{config.label}</span>
  );
};

const StatusBadge: React.FC<{ status: Rate["status"] }> = ({ status }) => {
  const statusConfig = {
    active: { bg: "bg-green-100", text: "text-green-700", label: "Actif", icon: CheckCircle },
    inactive: { bg: "bg-gray-100", text: "text-gray-700", label: "Inactif", icon: XCircle },
    expired: { bg: "bg-red-100", text: "text-red-700", label: "Expiré", icon: AlertTriangle },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </span>
  );
};

const FeeDisplay: React.FC<{ feeType: Rate["feeType"]; feeValue: number }> = ({ feeType, feeValue }) => {
  const Icon = feeType === "percentage" ? Percent : DollarSign;
  const value = feeType === "percentage" ? `${feeValue}%` : `${feeValue} CAD`;

  return (
    <div className="flex items-center space-x-1">
      <Icon className="w-4 h-4 text-gray-600" />
      <span className="font-medium text-gray-800">{value}</span>
    </div>
  );
};

// RateItem Component
const RateItem: React.FC<RateItemProps> = ({ rate, onAction }) => {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-4">
        <div className="flex items-center space-x-3">
          <ServiceBadge service={rate.service} />
          <div>
            <p className="font-medium text-gray-800">{rate.serviceName}</p>
          </div>
        </div>
      </td>
      <td className="px-6 py-4">
        {rate.country ? (
          <div className="flex items-center space-x-2">
            <span>{rate.countryFlag}</span>
            <span className="text-sm">{rate.country}</span>
          </div>
        ) : (
          <span className="text-gray-400 text-sm">Global</span>
        )}
      </td>
      <td className="px-6 py-4">
        <FeeDisplay feeType={rate.feeType} feeValue={rate.feeValue} />
      </td>
      <td className="px-6 py-4">
        <StatusBadge status={rate.status} />
      </td>
      <td className="px-6 py-4">
        <div className="text-sm">
          <p className="font-medium">{rate.usage.transactionsCount.toLocaleString()}</p>
          <p className="text-gray-600">{rate.usage.revenue.toLocaleString()} CAD</p>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center space-x-2">
          <button
            onClick={() => onAction("view", rate)}
            className="p-1 text-gray-400 hover:text-green-600 transition-colors"
            title="Voir détails"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={() => onAction("edit", rate)}
            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
            title="Modifier"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={() => onAction("toggle", rate)}
            className={`p-1 transition-colors ${
              rate.status === "active" ? "text-gray-400 hover:text-red-600" : "text-gray-400 hover:text-green-600"
            }`}
            title={rate.status === "active" ? "Désactiver" : "Activer"}
          >
            <Power className="w-4 h-4" />
          </button>
        </div>
      </td>
    </tr>
  );
};

// RatesList Component
const RatesList: React.FC<RatesListProps> = ({
  rates,
  filters,
  onFiltersChange,
  onRateAction,
  currentPage,
  totalPages,
  onPageChange,
  startIndex,
  endIndex,
  totalRates,
}) => {
  const ratesPerPage = 10;
  const startIdx = (currentPage - 1) * ratesPerPage;
  const currentRates = rates.slice(startIdx, startIdx + ratesPerPage);

  return (
    <>
      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par service..."
                value={filters.search}
                onChange={(e) => onFiltersChange({ ...filters, search: e.target.value })}
                className="pl-10 pr-4 py-2 w-full border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          <select
            value={filters.service}
            onChange={(e) => onFiltersChange({ ...filters, service: e.target.value })}
            className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="">Tous les services</option>
            <option value="standard">Standard</option>
            <option value="express">Express</option>
            <option value="p2p">P2P</option>
            <option value="exchange">Change</option>
          </select>

          <select
            value={filters.status}
            onChange={(e) => onFiltersChange({ ...filters, status: e.target.value })}
            className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actif</option>
            <option value="inactive">Inactif</option>
            <option value="expired">Expiré</option>
          </select>
        </div>
      </div>

      {/* Rates Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Service</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 w-32">Pays</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 w-32">Tarif</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 w-32">Statut</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 w-32">Usage</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-gray-600 w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {currentRates.map((rate) => (
                <RateItem key={rate.id} rate={rate} onAction={onRateAction} />
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Affichage {startIndex + 1}-{endIndex} sur {totalRates} tarifs
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Précédent
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} sur {totalPages}
              </span>
              <button
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-200 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Suivant
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// PromoCodesList Component with Edit and Delete
const PromoCodesList: React.FC<{
  promoCodes: PromoCode[];
  onEdit: (promo: PromoCode) => void;
  onDelete: (promo: PromoCode) => void;
  onAdd: () => void;
}> = ({ promoCodes, onEdit, onDelete, onAdd }) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-800">Codes Promo Actifs</h3>
          <button
            onClick={onAdd}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Code
          </button>
        </div>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {promoCodes.map((promo) => (
            <div key={promo.id} className="p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-100 p-2 rounded-lg">
                    <Tag className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">{promo.code}</p>
                    <p className="text-sm text-gray-600">{promo.description}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-semibold text-green-600">
                    {promo.discountType === "percentage" ? `${promo.discountValue}%` : `${promo.discountValue} CAD`}
                  </p>
                  <p className="text-sm text-gray-600">{promo.usageCount} utilisations</p>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <span>
                    Valide du {promo.validFrom} au {promo.validTo}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => onEdit(promo)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Modifier"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => onDelete(promo)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// RateDetails Component (Modal)
const RateDetails: React.FC<RateDetailsProps> = ({ rate, onClose, onSave, onToggleStatus }) => {
  if (!rate) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-bold text-gray-800">Configuration Tarif</h2>
            <ServiceBadge service={rate.service} />
            <StatusBadge status={rate.status} />
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-800 mb-3">Informations Générales</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Service:</span>
                  <span className="font-medium">{rate.serviceName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Pays:</span>
                  <span className="font-medium">{rate.country ? `${rate.countryFlag} ${rate.country}` : "Global"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tarif:</span>
                  <FeeDisplay feeType={rate.feeType} feeValue={rate.feeValue} />
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-800 mb-3">Validité</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Date d'effet:</span>
                  <span className="font-medium">{rate.effectiveDate}</span>
                </div>
                {rate.expiryDate && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date d'expiration:</span>
                    <span className="font-medium">{rate.expiryDate}</span>
                  </div>
                )}
                {rate.minFee && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Frais minimum:</span>
                    <span className="font-medium">{rate.minFee} CAD</span>
                  </div>
                )}
                {rate.maxFee && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Frais maximum:</span>
                    <span className="font-medium">{rate.maxFee} CAD</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Usage Statistics */}
          <div>
            <h3 className="font-semibold text-gray-800 mb-3">Statistiques d'Usage</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-2 mb-1">
                  <Activity className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-gray-600">Transactions</span>
                </div>
                <p className="text-xl font-bold text-green-600">{rate.usage.transactionsCount.toLocaleString()}</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <div className="flex items-center space-x-2 mb-1">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  <span className="text-sm text-gray-600">Revenus générés</span>
                </div>
                <p className="text-xl font-bold text-green-600">{rate.usage.revenue.toLocaleString()} CAD</p>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4 border-t">
            <button
              onClick={onToggleStatus}
              className={`flex-1 py-2 px-4 rounded-lg transition-colors ${
                rate.status === "active"
                  ? "bg-red-600 text-white hover:bg-red-700"
                  : "bg-green-600 text-white hover:bg-green-700"
              }`}
            >
              {rate.status === "active" ? "Désactiver Tarif" : "Activer Tarif"}
            </button>
            <button
              onClick={() => onSave(rate)}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Modifier Tarif
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main RatesPage Component
const RatesPage: React.FC = () => {
  const [promoCodes, setPromoCodes] = useState<PromoCode[]>([
    {
      id: "PC001",
      code: "WELCOME2024",
      description: "Promotion nouvel utilisateur",
      discountType: "percentage",
      discountValue: 50,
      usageCount: 567,
      validFrom: "2024-01-01",
      validTo: "2024-12-31",
    },
    {
      id: "PC002",
      code: "EXPRESS50",
      description: "Réduction transfert express",
      discountType: "fixed",
      discountValue: 10,
      usageCount: 234,
      validFrom: "2024-03-01",
      validTo: "2024-06-30",
    },
    {
      id: "PC003",
      code: "AFRICA2024",
      description: "Promotion pays africains",
      discountType: "percentage",
      discountValue: 25,
      usageCount: 1456,
      validFrom: "2024-02-01",
      validTo: "2024-05-31",
    },
  ]);

  // State management
  const [filters, setFilters] = useState<FilterState>({
    search: "",
    service: "",
    status: "",
  });

  const [selectedRateForDetails, setSelectedRateForDetails] = useState<Rate | null>(null);
  const [selectedPromoForEdit, setSelectedPromoForEdit] = useState<PromoCode | null>(null);
  const [promoToDelete, setPromoToDelete] = useState<PromoCode | null>(null);
  const [showPromoModal, setShowPromoModal] = useState(false);

  const handleSave = (rate: Rate) => {
    console.log("Saving rate:", rate.serviceName);
    setSelectedRateForDetails(null);
  };

  const handleToggleStatus = () => {
    console.log("Toggling rate status:", selectedRateForDetails?.serviceName);
    setSelectedRateForDetails(null);
  };

  // Promo code handlers
  const handleAddPromo = () => {
    setSelectedPromoForEdit(null);
    setShowPromoModal(true);
  };

  const handleEditPromo = (promo: PromoCode) => {
    setSelectedPromoForEdit(promo);
    setShowPromoModal(true);
  };

  const handleDeletePromo = (promo: PromoCode) => {
    setPromoToDelete(promo);
  };

  const confirmDeletePromo = () => {
    if (promoToDelete) {
      setPromoCodes(promoCodes.filter((p) => p.id !== promoToDelete.id));
      setPromoToDelete(null);
    }
  };

  const handleSavePromo = (promo: PromoCode) => {
    if (selectedPromoForEdit) {
      // Edit existing
      setPromoCodes(promoCodes.map((p) => (p.id === promo.id ? promo : p)));
    } else {
      // Add new
      setPromoCodes([...promoCodes, promo]);
    }
    setShowPromoModal(false);
    setSelectedPromoForEdit(null);
  };

  return (
    <Layout>
      <div className="p-2 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <TrendingUp className="w-8 h-8 mt-2 text-green-600" />
              Gestion des codes promos
            </h1>
            <p className="text-gray-600 mt-1">Configuration des tarifs, frais et codes promotionnels</p>
          </div>
        </div>

        {/* Promo Codes Section */}
        <PromoCodesList
          promoCodes={promoCodes}
          onEdit={handleEditPromo}
          onDelete={handleDeletePromo}
          onAdd={handleAddPromo}
        />

        {/* Promo Code Modal */}
        {showPromoModal && (
          <PromoCodeModal
            promo={selectedPromoForEdit}
            onClose={() => {
              setShowPromoModal(false);
              setSelectedPromoForEdit(null);
            }}
            onSave={handleSavePromo}
          />
        )}

        {/* Confirm Delete Modal */}
        {promoToDelete && (
          <ConfirmDeleteModal
            promoCode={promoToDelete.code}
            onConfirm={confirmDeletePromo}
            onCancel={() => setPromoToDelete(null)}
          />
        )}

        {/* Rate Details Modal */}
        <RateDetails
          rate={selectedRateForDetails}
          onClose={() => setSelectedRateForDetails(null)}
          onSave={handleSave}
          onToggleStatus={handleToggleStatus}
        />
      </div>
    </Layout>
  );
};

export default RatesPage;
